import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { toast } from "sonner";

import { searchKontragents } from "@/api/one_c_pickers";
import * as api from "@/api/system_settings";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { toastError } from "@/lib/api-errors";
import { useDebounced } from "@/lib/use-debounced";
import type { PalletTemplate } from "@/types/api";

const TEMPLATE_LABELS: Record<PalletTemplate, string> = {
  lenta: "Лента",
  miratorg: "Мираторг",
  podruzhka: "Подружка",
  familia: "Фамилия",
  lavka: "Яндекс.Лавка",
  zolotoe_yabloko: "Золотое Яблоко",
};
const TEMPLATE_ORDER = Object.keys(TEMPLATE_LABELS) as PalletTemplate[];

/** Живой поиск контрагента в 1С (серверный, по названию). */
function KontragentSearch({
  onPick,
}: {
  onPick: (k: { ref: string; name: string }) => void;
}): React.ReactElement {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const dq = useDebounced(q.trim(), 300);

  const searchQ = useQuery({
    queryKey: ["kontragent-search", dq],
    queryFn: () => searchKontragents(dq),
    enabled: dq.length >= 2,
    staleTime: 60_000,
  });

  return (
    <div className="relative">
      <Input
        placeholder="Поиск контрагента в 1С (от 2 символов)"
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && dq.length >= 2 && (
        <div className="absolute z-10 mt-1 max-h-64 w-full overflow-auto rounded border bg-popover p-1 shadow-md">
          {searchQ.isLoading && (
            <p className="p-2 text-sm text-muted-foreground">Поиск…</p>
          )}
          {searchQ.isError && (
            <p className="p-2 text-sm text-destructive">
              Ошибка поиска в 1С (нужно рабочее подключение у администратора).
            </p>
          )}
          {!searchQ.isLoading &&
            !searchQ.isError &&
            (searchQ.data ?? []).map((k) => (
              <button
                key={k.ref}
                type="button"
                className="block w-full rounded px-2 py-1.5 text-left text-sm hover:bg-accent"
                onClick={() => {
                  onPick({ ref: k.ref, name: k.name });
                  setQ("");
                  setOpen(false);
                }}
              >
                {k.name}
              </button>
            ))}
          {!searchQ.isLoading && !searchQ.isError && (searchQ.data ?? []).length === 0 && (
            <p className="p-2 text-sm text-muted-foreground">Ничего не найдено</p>
          )}
        </div>
      )}
    </div>
  );
}

/** Привязки «шаблон паллетного листа → контрагент»: один шаблон → много
 *  контрагентов. Форма: выбор шаблона + живой поиск контрагента. */
export function TemplateBindingsCard(): React.ReactElement {
  const qc = useQueryClient();
  const bindingsQ = useQuery({
    queryKey: ["template-bindings"],
    queryFn: api.listTemplateBindings,
  });
  const [template, setTemplate] = useState<PalletTemplate>("lenta");
  const [picked, setPicked] = useState<{ ref: string; name: string } | null>(null);

  const addMut = useMutation({
    mutationFn: api.addTemplateBinding,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["template-bindings"] });
      setPicked(null);
      toast.success("Привязка добавлена");
    },
    onError: toastError("Не удалось добавить привязку"),
  });
  const delMut = useMutation({
    mutationFn: api.deleteTemplateBinding,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["template-bindings"] });
      toast.success("Привязка удалена");
    },
    onError: toastError("Не удалось удалить привязку"),
  });

  const bindings = bindingsQ.data ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">
          Шаблоны паллетных листов по контрагентам
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Свяжите шаблон паллетного листа с контрагентом. Один шаблон можно
          привязать к нескольким контрагентам. Контрагент ищется живым поиском в
          1С по названию.
        </p>

        <div className="grid gap-2 sm:grid-cols-[minmax(0,14rem)_1fr_auto] sm:items-start">
          <select
            className="h-9 rounded-md border bg-background px-2 text-sm"
            value={template}
            onChange={(e) => setTemplate(e.target.value as PalletTemplate)}
          >
            {TEMPLATE_ORDER.map((t) => (
              <option key={t} value={t}>
                {TEMPLATE_LABELS[t]}
              </option>
            ))}
          </select>
          <div className="space-y-1">
            <KontragentSearch onPick={setPicked} />
            {picked && (
              <p className="text-xs text-muted-foreground">
                Выбран: <span className="font-medium">{picked.name}</span>
              </p>
            )}
          </div>
          <Button
            disabled={!picked || addMut.isPending}
            onClick={() =>
              picked &&
              addMut.mutate({
                template,
                kontragent_ref: picked.ref,
                kontragent_name: picked.name,
              })
            }
          >
            Добавить
          </Button>
        </div>

        <div className="space-y-3">
          {bindingsQ.isLoading && (
            <p className="text-sm text-muted-foreground">Загрузка привязок…</p>
          )}
          {bindings.length === 0 && !bindingsQ.isLoading && (
            <p className="text-sm text-muted-foreground">Привязок пока нет.</p>
          )}
          {TEMPLATE_ORDER.filter((t) => bindings.some((b) => b.template === t)).map(
            (t) => (
              <div key={t} className="rounded border p-2">
                <div className="mb-1 text-sm font-medium">{TEMPLATE_LABELS[t]}</div>
                <ul className="space-y-1">
                  {bindings
                    .filter((b) => b.template === t)
                    .map((b) => (
                      <li
                        key={b.id}
                        className="flex items-center justify-between gap-2 text-sm"
                      >
                        <span className="min-w-0 truncate">
                          {b.kontragent_name || b.kontragent_ref}
                        </span>
                        <Button
                          size="sm"
                          variant="ghost"
                          disabled={delMut.isPending}
                          onClick={() => delMut.mutate(b.id)}
                        >
                          ✕
                        </Button>
                      </li>
                    ))}
                </ul>
              </div>
            ),
          )}
        </div>
      </CardContent>
    </Card>
  );
}
